import React from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import { TONES } from './poIssuesData';

/**
 * PO Issues table (BCM-897 deck slides 2 & 4).
 *
 * Open tab  → ... | PO Status | Timestamp | Action        ("Mark as Resolved",
 *                                                          Delivery Mismatch only)
 * Closed tab → ... | PO Status | Timestamp | Resolved by  (no Action column)
 */

const chipClassFor = (tone, classes) => {
  if (tone === TONES.po) return classes.chipPo;
  if (tone === TONES.md) return classes.chipMd;
  return classes.chipNeutral;
};

const valueClassFor = (tone, classes) => {
  if (tone === TONES.po) return classes.valuePo;
  if (tone === TONES.md) return classes.valueMd;
  return classes.valueNeutral;
};

/* Keyed on poStatusID, matching the PO dashboard's renderPOStatusButton, so the
   same status is the same colour on both screens. This used to switch on the
   status TEXT against 'Confirmed' / 'Delivered' / 'Deleted' — but the status
   master holds ten values and has no 'Delivered' or 'Deleted' at all, so eight
   of them fell through to the default and were painted as Created. */
const STATUS_CLASS_BY_ID = {
  21: 'statusCreated',
  22: 'statusShared',
  23: 'statusUnderConf',
  24: 'statusRejected',
  25: 'statusAgreed',
  26: 'statusAwaiting',
  27: 'statusAgreed',
  29: 'statusAgreed',
  30: 'statusComplete',
  31: 'statusAgreed',
};

// An id we do not know reads as unknown rather than borrowing Created's colour —
// the old default asserted a status the row did not have.
const statusClassFor = (poStatusID, classes) =>
  classes[STATUS_CLASS_BY_ID[Number(poStatusID)]] || classes.statusUnknown;

// Loading placeholders. Each cell echoes the shape of the content it stands in
// for, so the table reads as itself rather than as generic grey bars.
const SkeletonRows = ({ classes, isClosed, count = 6 }) => {
  const bar = (width, extra) => (
    <span className={`${classes.skBar} ${extra || ''}`} style={width ? { width } : undefined} />
  );

  return Array.from({ length: count }).map((_, i) => (
    <TableRow key={`sk-${i}`}>
      <TableCell className={classes.td}>{bar('72%')}</TableCell>
      <TableCell className={classes.td}>{bar(i % 2 ? '78%' : '62%')}</TableCell>
      <TableCell className={classes.td}>
        <div className={classes.skStack}>
          <div className={classes.skInfoRow}>
            {bar(null, classes.skChip)}
            {bar('62%')}
          </div>
          {i % 3 !== 2 && (
            <div className={classes.skInfoRow}>
              {bar(null, classes.skChip)}
              {bar('48%')}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className={classes.td}>{bar('80%')}</TableCell>
      <TableCell className={classes.td}>
        <div className={classes.skStack}>
          {bar('44%')}
          {bar('92%')}
        </div>
      </TableCell>
      <TableCell className={classes.td}>{bar(null, classes.skPill)}</TableCell>
      <TableCell className={classes.td}>
        <div className={classes.skStack}>
          {bar('88%')}
          {bar('64%')}
        </div>
      </TableCell>
      <TableCell className={classes.td}>
        {isClosed ? (
          <div className={classes.skStack}>
            {bar('70%')}
            {bar('88%')}
            {bar('58%')}
          </div>
        ) : (
          i % 3 === 0 && bar(null, classes.skButton)
        )}
      </TableCell>
    </TableRow>
  ));
};

const PoIssuesTable = ({
  classes,
  rows,
  variant, // 'open' | 'closed'
  loading = false,
  resolvingId = null,
  onOpenProcessFlow,
  onMarkResolved,
}) => {
  const isClosed = variant === 'closed';
  const colCount = isClosed ? 8 : 8;

  return (
    <div className={classes.tableWrap}>
      <Table
        size="small"
        stickyHeader
        className={`${classes.table} default-table-styles`}
      >
        <TableHead>
          <TableRow>
            <TableCell className={classes.th}>PO Number</TableCell>
            <TableCell className={classes.th}>Alert</TableCell>
            <TableCell className={classes.th}>More Information</TableCell>
            <TableCell className={classes.th}>CMO</TableCell>
            <TableCell className={classes.th}>Product</TableCell>
            <TableCell className={classes.th}>PO Status</TableCell>
            <TableCell className={classes.th}>Timestamp</TableCell>
            <TableCell className={classes.th}>
              {isClosed ? 'Resolved by' : 'Action'}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <SkeletonRows classes={classes} isClosed={isClosed} />
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colCount} className={classes.emptyRow}>
                No PO alerts match the current filters.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.id} className={classes.row}>
                <TableCell className={classes.td}>
                  {/* Same treatment as the Inactive POs dashboard: red for a PO
                      marked for delete (shown as "Cancelled"), grey once
                      delivery is complete, and a hover card carrying the PO
                      status either way. Marked-for-delete wins over delivery
                      complete, matching that screen's own precedence. */}
                  <Tooltip
                    title={
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                          PO Status
                        </div>
                        <div>
                          {row.isMarkedForDelete
                            ? 'Cancelled'
                            : row.poStatus || 'NA'}
                        </div>
                      </div>
                    }
                    arrow
                    placement="right"
                  >
                    <span
                      className={`${classes.poNumber} ${
                        row.isMarkedForDelete
                          ? classes.poNumberDeleted
                          : row.isDeliveryComplete
                          ? classes.poNumberDeliveryComplete
                          : ''
                      }`}
                    >
                      {row.poNumber}
                    </span>
                  </Tooltip>
                </TableCell>

                <TableCell className={classes.td}>
                  <span className={classes.issueText}>{row.issue}</span>
                </TableCell>

                <TableCell className={classes.td}>
                  {/* Chip and value are direct children of the grid, not wrapped
                      per row — that is what lets the value column align. */}
                  <div className={classes.infoList}>
                    {(row.info || []).map((item, i) => (
                      <React.Fragment key={i}>
                        {/* A plain-sentence detail has no label, so no chip; its
                            value spans both columns instead. */}
                        {item.tag ? (
                          <span
                            className={`${classes.chip} ${chipClassFor(item.tone, classes)}`}
                          >
                            {item.tag}
                          </span>
                        ) : null}
                        <span
                          className={`${classes.infoValue} ${valueClassFor(item.tone, classes)} ${
                            item.tag ? '' : classes.infoFullWidth
                          }`}
                        >
                          {item.value}
                        </span>
                      </React.Fragment>
                    ))}
                    {row.showProcessFlowLink && (
                      <button
                        type="button"
                        className={`${classes.processFlowLink} ${classes.infoFullWidth}`}
                        onClick={onOpenProcessFlow}
                      >
                        Current process flow configurations
                      </button>
                    )}
                    {/* Some alerts carry no detail at all — a dash reads as
                        "nothing to show" where a blank cell reads as broken. */}
                    {!(row.info || []).length && !row.showProcessFlowLink && (
                      <span className={`${classes.emptyCell} ${classes.infoFullWidth}`}>—</span>
                    )}
                  </div>
                </TableCell>

                <TableCell className={classes.td}>{row.cmo}</TableCell>

                <TableCell className={classes.td}>
                  <span className={classes.productCode}>{row.productCode}</span>
                  <span className={classes.productDesc}>
                    {row.productDescription}
                  </span>
                </TableCell>

                <TableCell className={classes.td}>
                  {/* An empty poStatusText would otherwise paint a blank pill. */}
                  {row.poStatus ? (
                    <span
                      className={`${classes.statusPill} ${statusClassFor(row.poStatusID, classes)}`}
                    >
                      {row.poStatus}
                    </span>
                  ) : (
                    <span className={classes.stampLine}>—</span>
                  )}
                </TableCell>

                <TableCell className={classes.td}>
                  <span className={classes.stampLine}>{row.timestamp?.date}</span>
                  <span className={classes.stampTime}>{row.timestamp?.time}</span>
                </TableCell>

                <TableCell className={classes.td}>
                  {isClosed ? (
                    row.resolvedBy ? (
                      <>
                        <span className={classes.resolvedName}>
                          {row.resolvedBy.name}
                        </span>
                        <span className={classes.resolvedStamp}>
                          {row.resolvedBy.date}
                        </span>
                        <span className={classes.resolvedStamp}>
                          {row.resolvedBy.time}
                        </span>
                      </>
                    ) : null
                  ) : (
                    // The API decides which alerts are resolvable, via
                    // showMarkAsResolved on each row.
                    row.showMarkAsResolved && (
                      <Button
                        variant="contained"
                        className={classes.resolveBtn}
                        onClick={() => onMarkResolved(row)}
                        disabled={resolvingId === row.id}
                        disableElevation
                      >
                        {resolvingId === row.id ? 'Resolving…' : 'Mark as Resolved'}
                      </Button>
                    )
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default PoIssuesTable;
